import React from "react";
import { TierName, TIER_DISPLAY } from "../types/AIModel";

export interface PricingFeature {
  text: string;
  included: boolean;
  highlight?: boolean;
}

export interface PricingCardProps {
  tier: TierName;
  name: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  currency?: string; // VND, USD, etc.
  features: PricingFeature[];
  isCurrentPlan?: boolean;
  selectedInterval: "month" | "year";
  onSelect?: () => void;
  loading?: boolean;
  popular?: boolean;
  disabled?: boolean;
}

// Format price based on currency
const formatPrice = (amount: number, currency: string = 'USD'): string => {
  if (currency === 'VND') {
    // Format VND with thousand separators, no decimals
    return amount.toLocaleString('vi-VN');
  }
  // Default USD format
  return amount.toFixed(2);
};

// Get currency symbol
const getCurrencySymbol = (currency: string = 'USD'): string => {
  switch (currency) {
    case 'VND':
      return '₫';
    case 'USD':
    default:
      return '$';
  }
};

const PricingCard: React.FC<PricingCardProps> = ({
  tier,
  name,
  monthlyPrice,
  yearlyPrice,
  currency = 'USD',
  features,
  isCurrentPlan,
  selectedInterval,
  onSelect,
  loading,
  popular,
  disabled,
}) => {
  const tierDisplay = TIER_DISPLAY[tier];
  const price = selectedInterval === "month" ? monthlyPrice : yearlyPrice;
  const isFree = tier === "free";
  const currencySymbol = getCurrencySymbol(currency);

  // Calculate savings for yearly
  const yearlySavings =
    monthlyPrice && yearlyPrice
      ? Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100)
      : 0;

  return (
    <div
      className={`relative flex flex-col rounded-2xl p-6 transition-all duration-300 ${
        popular
          ? "bg-gradient-to-b from-[#3af062]/20 to-transparent border-2 border-[#3af062] scale-105 z-10"
          : "bg-[#1a1a1a] border border-gray-700 hover:border-gray-500"
      }`}
    >
      {/* Popular badge */}
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#3af062] text-black text-xs font-bold rounded-full">
          MOST POPULAR
        </div>
      )}

      {/* Current plan badge */}
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
          CURRENT PLAN
        </div>
      )}

      {/* Tier badge */}
      <div
        className="w-fit px-3 py-1 rounded-full text-xs font-bold mb-4"
        style={{
          backgroundColor: tierDisplay.bgColor,
          color: tierDisplay.color,
        }}
      >
        {tierDisplay.label}
      </div>

      {/* Plan name */}
      <h3 className="text-white text-xl font-bold mb-2">{name}</h3>

      {/* Price */}
      <div className="mb-6">
        {isFree ? (
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-white">Free</span>
          </div>
        ) : price !== null ? (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-white">
              {currency === 'VND' ? (
                <>{formatPrice(price, currency)}<span className="text-xl">₫</span></>
              ) : (
                <>{currencySymbol}{formatPrice(price, currency)}</>
              )}
            </span>
            <span className="text-gray-400">
              /{selectedInterval === "month" ? "mo" : "yr"}
            </span>
          </div>
        ) : (
          <div className="text-gray-400">Not available</div>
        )}

        {/* Yearly savings */}
        {!isFree && selectedInterval === "year" && yearlySavings > 0 && (
          <div className="mt-1 text-sm text-[#3af062]">
            Save {yearlySavings}% compared to monthly
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="flex-1 space-y-3 mb-6">
        {features.map((feature, index) => (
          <li
            key={index}
            className={`flex items-start gap-3 ${
              feature.included ? "text-white" : "text-gray-500"
            }`}
          >
            {feature.included ? (
              <svg
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  feature.highlight ? "text-[#3af062]" : "text-[#3af062]"
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span className={feature.highlight ? "font-semibold" : ""}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      {isFree ? (
        <button
          disabled
          className="w-full py-3 px-4 rounded-xl bg-gray-700 text-gray-400 font-semibold cursor-not-allowed"
        >
          Current Plan
        </button>
      ) : isCurrentPlan ? (
        <button
          onClick={onSelect}
          className="w-full py-3 px-4 rounded-xl bg-gray-600 text-white font-semibold hover:bg-gray-500 transition-colors"
        >
          Manage Subscription
        </button>
      ) : (
        <button
          onClick={onSelect}
          disabled={loading || price === null || disabled}
          className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
            popular
              ? "bg-[#3af062] text-black hover:bg-[#2dd54f]"
              : "bg-white text-black hover:bg-gray-200"
          } ${loading || price === null || disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </span>
          ) : (
            `Upgrade to ${name}`
          )}
        </button>
      )}
    </div>
  );
};

export default PricingCard;
